import React, { useEffect, useMemo } from 'react';
import { FilterProvider, useFilters } from './context/FilterContext';
import { BoardProvider, useBoardState, useBoardActions } from './context/BoardContext';
import { BoardSectionProvider, useBoardSection } from './context/BoardSectionContext';
import { TaskProvider } from './context/TaskContext';
import { SpinnerComponent } from 'shared/SpinnerComponent';
import { BoardContent } from './components/Board';

import { setAppTitle } from 'utils/utils';
import useComponentLifecycle from 'hooks/useComponentLifecycle';
import ErrorFallback from 'components/ErrorFallback/ErrorFallback';

const BoardWrapper = () => {
  const { initialLoading, error } = useBoardState();
  const { resetBoard } = useBoardActions();

  if (initialLoading) {
    return <SpinnerComponent />;
  }

  if (error) {
    return <ErrorFallback resetErrorBoundary={resetBoard} />;
  }

  return <BoardContent />;
};

const BoardWithFilters = () => {
  const filters = useFilters();
  const { caseDetails } = useBoardSection();

  // Build filter object for API - memoized to prevent unnecessary rerenders and query refetches
  const filterParams = useMemo(
    () => ({
      searchKeyword: filters.searchKeyword,
      practiceArea: filters.practiceArea,
      assignedTo: filters.assignedTo,
      sortType: filters.sortType,
      dateFilter: filters.dateFilter,
      priority: filters.priority,
      case_id: caseDetails?.case_id || undefined,
    }),
    [
      filters.searchKeyword,
      filters.practiceArea,
      filters.assignedTo,
      filters.sortType,
      filters.dateFilter,
      filters.priority,
      caseDetails?.case_id,
    ]
  );

  return (
    <BoardProvider filters={filterParams}>
      <TaskProvider>
        <BoardWrapper />
      </TaskProvider>
    </BoardProvider>
  );
};

/**
 * Main TaskBoard Component
 * @param {Object} props
 * @param {boolean} props.isFromLead - Is board mounted in Lead section (default: false)
 * @param {boolean} props.isFromCase - Is board mounted in Case section (default: false)
 * @param {Object} props.caseDetails - Case/Lead details object (optional)
 */
const TaskBoard = ({ isFromLead = false, isFromCase = false, caseDetails = null, onMountUnMount }) => {
  useComponentLifecycle('task-activity', onMountUnMount);
  useEffect(() => {
    setAppTitle('Tasks');

    return () => {
      setAppTitle('');
    };
  }, []);

  return (
    <BoardSectionProvider isFromLead={isFromLead} isFromCase={isFromCase} caseDetails={caseDetails}>
      <FilterProvider>
        <BoardWithFilters />
      </FilterProvider>
    </BoardSectionProvider>
  );
};

export default TaskBoard;
